<template>
    <div class="page">
        <div class="page-header">
            <div>
                <h2 class="page-title">{{ t('members') }}</h2>
                <p class="page-subtitle">{{ t('members_subtitle') }}</p>
            </div>
        </div>

        <div class="card">
            <div class="datatable-toolbar">
                <div class="toolbar-left">
                    <select v-model.number="perPage" class="length-select" :disabled="loading">
                        <option :value="10">10</option>
                        <option :value="20">20</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                    </select>

                    <select v-model="statusFilter" class="status-select" :disabled="loading">
                        <option value="all">{{ t('all') }}</option>
                        <option value="active">{{ t('active') }}</option>
                        <option value="inactive">{{ t('inactive') }}</option>
                    </select>

                    <button type="button" class="tool-btn tool-btn-icon" @click="toggleFilters" :disabled="loading">
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                            <path d="M2.5 4.16667H17.5M5.83333 10H14.1667M8.33333 15.8333H11.6667" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>

                    <div class="columns-wrap">
                        <button type="button" class="tool-btn tool-btn-light" @click="toggleColumns" :disabled="loading">COLUMNS</button>
                        <div v-if="showColumns" class="columns-dropdown">
                            <label v-for="c in columns" :key="c.key" class="columns-item">
                                <input type="checkbox" v-model="visibleColumns[c.key]" :disabled="c.locked">
                                <span>{{ t(c.labelKey) }}</span>
                            </label>
                        </div>
                    </div>

                    <button type="button" class="tool-btn tool-btn-primary" @click="load" :disabled="loading">{{ t('refresh') }}</button>
                    <button type="button" class="tool-btn tool-btn-light" @click="clearFilters" :disabled="!hasAnyFilter">{{ t('clear_filters') }}</button>
                    <button type="button" class="tool-btn tool-btn-success" @click="openCreate" :disabled="loading">{{ t('add_member') }}</button>
                </div>

                <div class="toolbar-right">
                    <div class="search-box">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M17.5 17.5L13.875 13.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        <input v-model="searchQuery" type="text" :placeholder="t('search') + '...'" class="search-input" @input="debouncedSearch" :disabled="loading">
                    </div>
                </div>
            </div>

            <div class="table-container">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th v-if="visibleColumns.id">
                                <input v-if="showFilters" v-model="colFilters.id" type="text" class="th-input" :placeholder="t('id')">
                                <span v-else>{{ t('id') }}</span>
                            </th>
                            <th v-if="visibleColumns.customer_name">
                                <input v-if="showFilters" v-model="colFilters.customer_name" type="text" class="th-input" :placeholder="t('customer_name')">
                                <span v-else>{{ t('customer_name') }}</span>
                            </th>
                            <th v-if="visibleColumns.member_type">
                                <input v-if="showFilters" v-model="colFilters.member_type" type="text" class="th-input" :placeholder="t('member_type')">
                                <span v-else>{{ t('member_type') }}</span>
                            </th>
                            <th v-if="visibleColumns.category">
                                <input v-if="showFilters" v-model="colFilters.category" type="text" class="th-input" :placeholder="t('category')">
                                <span v-else>{{ t('category') }}</span>
                            </th>
                            <th v-if="visibleColumns.mobile">
                                <input v-if="showFilters" v-model="colFilters.mobile" type="text" class="th-input" :placeholder="t('mobile')">
                                <span v-else>{{ t('mobile') }}</span>
                            </th>
                            <th v-if="visibleColumns.email">
                                <input v-if="showFilters" v-model="colFilters.email" type="text" class="th-input" :placeholder="t('email')">
                                <span v-else>{{ t('email') }}</span>
                            </th>
                            <th v-if="visibleColumns.district">
                                <input v-if="showFilters" v-model="colFilters.district" type="text" class="th-input" :placeholder="t('district')">
                                <span v-else>{{ t('district') }}</span>
                            </th>
                            <th v-if="visibleColumns.status">
                                <select v-if="showFilters" v-model="colFilters.status" class="th-select">
                                    <option value="">{{ t('status') }}</option>
                                    <option value="active">{{ t('active') }}</option>
                                    <option value="inactive">{{ t('inactive') }}</option>
                                </select>
                                <span v-else>{{ t('status') }}</span>
                            </th>
                            <th v-if="visibleColumns.actions">{{ t('actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading">
                            <td :colspan="visibleColumnCount" style="padding: 10px;">
                                <div class="skeleton-wrap">
                                    <div v-for="n in 6" :key="n" class="skeleton-row"></div>
                                </div>
                            </td>
                        </tr>
                        <tr v-for="row in filteredItems" v-if="!loading" :key="row.CustomerID">
                            <td v-if="visibleColumns.id">{{ row.CustomerID }}</td>
                            <td v-if="visibleColumns.customer_name">
                                <div class="primary">
                                    <div class="primary-title">{{ row.CustomerName }}</div>
                                    <div class="primary-sub">{{ row.CustomerCode || '-' }}</div>
                                </div>
                            </td>
                            <td v-if="visibleColumns.member_type">{{ row.MemberType }}</td>
                            <td v-if="visibleColumns.category">{{ row.MembershipCategory }}</td>
                            <td v-if="visibleColumns.mobile">{{ row.MobileNo }}</td>
                            <td v-if="visibleColumns.email">{{ row.Email || '-' }}</td>
                            <td v-if="visibleColumns.district">{{ districtLabel(row) }}</td>
                            <td v-if="visibleColumns.status">
                                <span :class="['tag', row.Status ? 'tag-success' : 'tag-gray']">
                                    {{ row.Status ? t('active') : t('inactive') }}
                                </span>
                            </td>
                            <td v-if="visibleColumns.actions">
                                <div class="action-buttons">
                                    <button type="button" class="btn btn-sm btn-secondary" @click="openView(row.CustomerID)">
                                        {{ t('view') }}
                                    </button>
                                    <button type="button" class="btn btn-sm btn-secondary" @click="openEdit(row.CustomerID)">
                                        {{ t('edit') }}
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-sm"
                                        :class="row.Status ? 'btn-warning' : 'btn-success'"
                                        @click="toggleStatus(row)"
                                        :disabled="busyIds[row.CustomerID] === true"
                                    >
                                        {{ row.Status ? t('deactivate') : t('activate') }}
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-danger"
                                        @click="remove(row.CustomerID)"
                                        :disabled="busyIds[row.CustomerID] === true"
                                    >
                                        {{ t('delete') }}
                                    </button>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="!loading && filteredItems.length === 0">
                            <td :colspan="visibleColumnCount" class="empty">{{ t('no_data') }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="pager">
                <div class="pager-left">{{ showingText }}</div>
                <div class="pager-right">
                    <button class="pager-btn" @click="goFirst" :disabled="loading || page <= 1">{{ t('first') }}</button>
                    <button class="pager-btn" @click="prevPage" :disabled="loading || page <= 1">{{ t('previous') }}</button>
                    <button v-for="p in pageButtons" :key="p" class="pager-num" :class="{ active: p === page }" @click="goPage(p)" :disabled="loading">
                        {{ p }}
                    </button>
                    <button class="pager-btn" @click="nextPage" :disabled="loading || page >= totalPages">{{ t('next') }}</button>
                    <button class="pager-btn" @click="goLast" :disabled="loading || page >= totalPages">{{ t('last') }}</button>
                </div>
            </div>
        </div>

        <Teleport to="body">
            <div v-if="showViewModal" class="modal-backdrop" @click.self="closeView">
                <div class="modal-card">
                    <div class="modal-head">
                        <div class="modal-title">{{ t('member_details') }}</div>
                        <button type="button" class="modal-close" @click="closeView">×</button>
                    </div>
                    <div class="modal-body" v-if="viewItem">
                        <div class="details-grid">
                            <div class="detail">
                                <div class="detail-label">{{ t('id') }}</div>
                                <div class="detail-value">{{ viewItem.CustomerID }}</div>
                            </div>
                            <div class="detail">
                                <div class="detail-label">{{ t('customer_name') }}</div>
                                <div class="detail-value">{{ viewItem.CustomerName }}</div>
                            </div>
                            <div class="detail" v-if="viewItem.MemberType === 'Corporate'">
                                <div class="detail-label">{{ t('organization_name') }}</div>
                                <div class="detail-value">{{ viewItem.OrganizationName || '-' }}</div>
                            </div>
                            <div class="detail">
                                <div class="detail-label">{{ t('member_type') }}</div>
                                <div class="detail-value">{{ viewItem.MemberType }}</div>
                            </div>
                            <div class="detail">
                                <div class="detail-label">{{ t('category') }}</div>
                                <div class="detail-value">{{ viewItem.MembershipCategory }}</div>
                            </div>
                            <div class="detail">
                                <div class="detail-label">{{ t('mobile') }}</div>
                                <div class="detail-value">{{ viewItem.MobileNo }}</div>
                            </div>
                            <div class="detail">
                                <div class="detail-label">{{ t('email') }}</div>
                                <div class="detail-value">{{ viewItem.Email || '-' }}</div>
                            </div>
                            <div class="detail">
                                <div class="detail-label">{{ t('district') }}</div>
                                <div class="detail-value">{{ districtLabel(viewItem) }}</div>
                            </div>
                            <div class="detail">
                                <div class="detail-label">{{ t('residence') }}</div>
                                <div class="detail-value">{{ viewItem.IsCity ? t('inside_city') : t('outside_city') }}</div>
                            </div>
                            <div class="detail">
                                <div class="detail-label">{{ t('nationality') }}</div>
                                <div class="detail-value">{{ viewItem.IsForeigner ? t('others') : t('bangladeshi') }}</div>
                            </div>
                            <div class="detail" v-if="viewItem.IsForeigner">
                                <div class="detail-label">{{ t('country') }}</div>
                                <div class="detail-value">{{ viewItem.CountryName || '-' }}</div>
                            </div>
                            <div class="detail">
                                <div class="detail-label">{{ t('status') }}</div>
                                <div class="detail-value">{{ viewItem.Status ? t('active') : t('inactive') }}</div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-actions">
                        <button type="button" class="btn btn-secondary" @click="closeView">{{ t('close') }}</button>
                    </div>
                </div>
            </div>

            <div v-if="showFormModal" class="modal-backdrop" @click.self="closeForm">
                <div class="modal-card">
                    <div class="modal-head">
                        <div class="modal-title">{{ formMode === 'create' ? t('add_member') : t('edit_member') }}</div>
                        <button type="button" class="modal-close" @click="closeForm">×</button>
                    </div>
                    <div class="modal-body">
                        <div class="form-grid">
                            <div class="form-group">
                                <label class="form-label">{{ t('member_type') }}</label>
                                <select v-model="form.member_type" class="form-input" @change="applyConditional">
                                    <option value="Corporate">{{ t('corporate') }}</option>
                                    <option value="Personal">{{ t('personal') }}</option>
                                </select>
                            </div>

                            <div class="form-group" v-if="form.member_type === 'Corporate'">
                                <label class="form-label">{{ t('organization_name') }}</label>
                                <input v-model="form.organization_name" type="text" class="form-input">
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('customer_name') }}</label>
                                <input v-model="form.customer_name" type="text" class="form-input">
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('mobile') }}</label>
                                <input v-model="form.mobile_no" type="text" class="form-input">
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('email') }}</label>
                                <input v-model="form.email" type="email" class="form-input">
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('district') }}</label>
                                <select v-model="form.district_id" class="form-input">
                                    <option value="" disabled>{{ t('select') }}</option>
                                    <option v-for="d in districts" :key="d.District_ID" :value="d.District_ID">{{ districtOptionLabel(d) }}</option>
                                </select>
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('residence') }}</label>
                                <select v-model="form.is_city" class="form-input">
                                    <option :value="true">{{ t('inside_city') }}</option>
                                    <option :value="false">{{ t('outside_city') }}</option>
                                </select>
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('category') }}</label>
                                <select v-model="form.membership_category" class="form-input">
                                    <option value="Owner">{{ t('owner') }}</option>
                                    <option value="Seeker">{{ t('seeker') }}</option>
                                </select>
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('nationality') }}</label>
                                <select v-model="form.is_foreigner" class="form-input" @change="applyConditional">
                                    <option :value="false">{{ t('bangladeshi') }}</option>
                                    <option :value="true">{{ t('others') }}</option>
                                </select>
                            </div>

                            <div class="form-group" v-if="form.is_foreigner">
                                <label class="form-label">{{ t('country') }}</label>
                                <input v-model="form.country_name" type="text" class="form-input">
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('status') }}</label>
                                <select v-model="form.status" class="form-input">
                                    <option :value="true">{{ t('active') }}</option>
                                    <option :value="false">{{ t('inactive') }}</option>
                                </select>
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('password') }}<span v-if="formMode === 'edit'" class="hint">({{ t('optional') }})</span></label>
                                <input v-model="form.password" type="password" class="form-input">
                            </div>
                        </div>
                    </div>
                    <div class="modal-actions">
                        <button type="button" class="btn btn-secondary" @click="closeForm">{{ t('cancel') }}</button>
                        <button type="button" class="btn btn-primary" @click="save" :disabled="saving">{{ t('save') }}</button>
                    </div>
                </div>
            </div>

            <div class="toast-container">
                <TransitionGroup name="toast" tag="div">
                    <div v-for="toast in toasts" :key="toast.id" :class="['toast', `toast-${toast.type}`]">
                        <div class="toast-content">
                            <p class="toast-message">{{ toast.message }}</p>
                        </div>
                        <button @click="removeToast(toast.id)" class="toast-close">
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                    </div>
                </TransitionGroup>
            </div>
        </Teleport>
    </div>
</template>

<script>
import { computed, inject, onMounted, ref, watch } from 'vue';

export default {
    name: 'CustomersPage',
    setup() {
        const adminT = inject('adminT', (k) => k);
        const adminLang = inject('adminLang', ref('en'));

        const t = (key) => adminT(key);
        const langValue = () => (adminLang?.value === 'bn' ? 'bn' : 'en');

        const items = ref([]);
        const districts = ref([]);
        const pagination = ref({ page: 1, per_page: 20, total: 0 });
        const page = ref(1);
        const perPage = ref(20);
        const statusFilter = ref('all');
        const searchQuery = ref('');
        const loading = ref(true);
        const saving = ref(false);
        const busyIds = ref({});
        let searchTimer = null;
        const showColumns = ref(false);
        const showFilters = ref(true);
        const columns = ref([
            { key: 'id', labelKey: 'id', locked: true },
            { key: 'customer_name', labelKey: 'customer_name', locked: true },
            { key: 'member_type', labelKey: 'member_type', locked: false },
            { key: 'category', labelKey: 'category', locked: false },
            { key: 'mobile', labelKey: 'mobile', locked: false },
            { key: 'email', labelKey: 'email', locked: false },
            { key: 'district', labelKey: 'district', locked: false },
            { key: 'status', labelKey: 'status', locked: false },
            { key: 'actions', labelKey: 'actions', locked: true },
        ]);
        const visibleColumns = ref({
            id: true,
            customer_name: true,
            member_type: true,
            category: true,
            mobile: true,
            email: true,
            district: true,
            status: true,
            actions: true,
        });
        const colFilters = ref({
            id: '',
            customer_name: '',
            member_type: '',
            category: '',
            mobile: '',
            email: '',
            district: '',
            status: '',
        });

        const showViewModal = ref(false);
        const viewItem = ref(null);
        const showFormModal = ref(false);
        const formMode = ref('create');
        const editingId = ref(null);

        const form = ref({
            member_type: 'Personal',
            membership_category: 'Owner',
            organization_name: '',
            customer_name: '',
            mobile_no: '',
            email: '',
            district_id: '',
            is_city: true,
            is_foreigner: false,
            country_name: '',
            password: '',
            status: true,
        });

        const normalizeBool = (v) => {
            if (typeof v === 'boolean') return v;
            if (typeof v === 'number') return v === 1;
            if (typeof v === 'string') return v === '1' || v.toLowerCase() === 'true';
            return !!v;
        };

        const applyConditional = () => {
            if (form.value.member_type !== 'Corporate') {
                form.value.organization_name = '';
            }
            if (!form.value.is_foreigner) {
                form.value.country_name = '';
            }
        };

        const addToast = (type, message) => {
            const id = Date.now() + Math.random();
            toasts.value.push({ id, type, message });
            setTimeout(() => removeToast(id), 4500);
        };

        const toasts = ref([]);
        const removeToast = (id) => {
            toasts.value = toasts.value.filter((x) => x.id !== id);
        };

        const loadDistricts = async () => {
            try {
                const res = await window.axios.get('/admin/api/districts', { params: { lang: langValue() } });
                districts.value = res.data?.data?.items || [];
            } catch {
                districts.value = [];
            }
        };

        const load = async () => {
            loading.value = true;
            try {
                const res = await window.axios.get('/admin/api/customers', {
                    params: {
                        status: statusFilter.value,
                        page: page.value,
                        per_page: perPage.value,
                        q: searchQuery.value || '',
                        lang: langValue(),
                    },
                });
                items.value = res.data?.data?.items || [];
                const nextPagination = res.data?.data?.pagination || { page: 1, per_page: perPage.value, total: 0 };
                pagination.value = nextPagination;
                page.value = Number(nextPagination.page || 1);
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed_to_load'));
            } finally {
                loading.value = false;
            }
        };

        const filteredItems = computed(() => {
            const list = Array.isArray(items.value) ? items.value : [];
            const f = colFilters.value || {};
            const normalize = (v) => (v ?? '').toString().toLowerCase();

            const idQ = normalize(f.id).trim();
            const nameQ = normalize(f.customer_name).trim();
            const typeQ = normalize(f.member_type).trim();
            const catQ = normalize(f.category).trim();
            const mobileQ = normalize(f.mobile).trim();
            const emailQ = normalize(f.email).trim();
            const distQ = normalize(f.district).trim();
            const statusQ = (f.status || '').toString().trim().toLowerCase();

            if (!idQ && !nameQ && !typeQ && !catQ && !mobileQ && !emailQ && !distQ && !statusQ) return list;

            return list.filter((row) => {
                const id = normalize(row.CustomerID);
                const name = normalize(row.CustomerName);
                const code = normalize(row.CustomerCode);
                const org = normalize(row.OrganizationName);
                const type = normalize(row.MemberType);
                const cat = normalize(row.MembershipCategory);
                const mobile = normalize(row.MobileNo);
                const email = normalize(row.Email);
                const district = normalize(districtLabel(row));
                const isActive = !!row.Status;

                if (idQ && !id.includes(idQ)) return false;
                if (nameQ && !(name.includes(nameQ) || code.includes(nameQ) || org.includes(nameQ))) return false;
                if (typeQ && !type.includes(typeQ)) return false;
                if (catQ && !cat.includes(catQ)) return false;
                if (mobileQ && !mobile.includes(mobileQ)) return false;
                if (emailQ && !email.includes(emailQ)) return false;
                if (distQ && !district.includes(distQ)) return false;
                if (statusQ === 'active' && !isActive) return false;
                if (statusQ === 'inactive' && isActive) return false;
                return true;
            });
        });

        const visibleColumnCount = computed(() => {
            const v = visibleColumns.value || {};
            return Object.values(v).filter(Boolean).length || 1;
        });

        const hasAnyFilter = computed(() => {
            if ((searchQuery.value || '').trim() !== '') return true;
            if ((statusFilter.value || 'all') !== 'all') return true;
            const f = colFilters.value || {};
            return Object.values(f).some((x) => String(x || '').trim() !== '');
        });

        const toggleFilters = () => {
            showFilters.value = !showFilters.value;
        };

        const toggleColumns = () => {
            showColumns.value = !showColumns.value;
        };

        const clearFilters = async () => {
            searchQuery.value = '';
            statusFilter.value = 'all';
            colFilters.value = { id: '', customer_name: '', member_type: '', category: '', mobile: '', email: '', district: '', status: '' };
            page.value = 1;
            await load();
        };

        const totalPages = computed(() => {
            const total = Number(pagination.value?.total || 0);
            const size = Number(perPage.value || 20);
            return Math.max(1, Math.ceil(total / Math.max(1, size)));
        });

        const pageButtons = computed(() => {
            const current = Math.min(Math.max(1, page.value || 1), totalPages.value);
            const total = totalPages.value;
            const max = 5;
            let start = Math.max(1, current - Math.floor(max / 2));
            let end = start + max - 1;
            if (end > total) {
                end = total;
                start = Math.max(1, end - max + 1);
            }
            const out = [];
            for (let p = start; p <= end; p++) out.push(p);
            return out;
        });

        const showingText = computed(() => {
            const total = Number(pagination.value?.total || 0);
            const size = Number(perPage.value || 20);
            const current = Math.min(Math.max(1, page.value || 1), totalPages.value);
            const from = total === 0 ? 0 : (current - 1) * size + 1;
            const to = total === 0 ? 0 : Math.min(current * size, total);
            const tpl = t('showing_entries');
            const base = tpl && tpl !== 'showing_entries' && tpl.includes('{from}')
                ? tpl.replace('{from}', String(from)).replace('{to}', String(to)).replace('{total}', String(total))
                : `Showing ${from} to ${to} of ${total} entries`;

            if (!hasAnyFilter.value) return base;
            const filteredTpl = t('filtered');
            const suffix = filteredTpl && filteredTpl !== 'filtered' && filteredTpl.includes('{count}')
                ? filteredTpl.replace('{count}', String(filteredItems.value.length))
                : `(filtered: ${filteredItems.value.length})`;
            return `${base} ${suffix}`;
        });

        const goPage = async (p) => {
            const next = Math.min(Math.max(1, Number(p || 1)), totalPages.value);
            if (next === page.value) return;
            page.value = next;
            await load();
        };

        const nextPage = async () => {
            if (page.value >= totalPages.value) return;
            page.value += 1;
            await load();
        };

        const prevPage = async () => {
            if (page.value <= 1) return;
            page.value -= 1;
            await load();
        };

        const goFirst = async () => {
            if (page.value <= 1) return;
            page.value = 1;
            await load();
        };

        const goLast = async () => {
            if (page.value >= totalPages.value) return;
            page.value = totalPages.value;
            await load();
        };

        const debouncedSearch = () => {
            if (searchTimer) clearTimeout(searchTimer);
            searchTimer = setTimeout(() => {
                page.value = 1;
                load();
            }, 350);
        };

        watch(perPage, async () => {
            page.value = 1;
            await load();
        });

        watch(statusFilter, async () => {
            page.value = 1;
            await load();
        });

        const districtOptionLabel = (d) => {
            const isBn = adminLang?.value === 'bn';
            return isBn ? (d.District_Name_BN || d.District_Name) : (d.District_Name || d.District_Name_BN);
        };

        const districtLabel = (row) => {
            const isBn = adminLang?.value === 'bn';
            const bn = row.District_Name_BN || row.District_Name || '';
            const en = row.District_Name || row.District_Name_BN || '';
            return isBn ? (bn || '-') : (en || '-');
        };

        const resetForm = () => {
            form.value = {
                member_type: 'Personal',
                membership_category: 'Owner',
                organization_name: '',
                customer_name: '',
                mobile_no: '',
                email: '',
                district_id: '',
                is_city: true,
                is_foreigner: false,
                country_name: '',
                password: '',
                status: true,
            };
            editingId.value = null;
        };

        const openCreate = () => {
            formMode.value = 'create';
            resetForm();
            showFormModal.value = true;
        };

        const openEdit = async (customerId) => {
            formMode.value = 'edit';
            resetForm();
            saving.value = true;
            try {
                const res = await window.axios.get(`/admin/api/customers/${customerId}`, { params: { lang: langValue() } });
                const item = res.data?.data;
                if (!item) throw new Error('Not found');
                editingId.value = item.CustomerID;
                form.value.member_type = item.MemberType || 'Personal';
                form.value.membership_category = item.MembershipCategory || 'Owner';
                form.value.organization_name = item.OrganizationName || '';
                form.value.customer_name = item.CustomerName || '';
                form.value.mobile_no = item.MobileNo || '';
                form.value.email = item.Email || '';
                form.value.district_id = item.DistrictID ? String(item.DistrictID) : '';
                form.value.is_city = normalizeBool(item.IsCity);
                form.value.is_foreigner = normalizeBool(item.IsForeigner);
                form.value.country_name = item.CountryName || '';
                form.value.status = normalizeBool(item.Status);
                form.value.password = '';
                applyConditional();
                showFormModal.value = true;
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed_to_load'));
            } finally {
                saving.value = false;
            }
        };

        const openView = async (customerId) => {
            saving.value = true;
            try {
                const res = await window.axios.get(`/admin/api/customers/${customerId}`, { params: { lang: langValue() } });
                viewItem.value = res.data?.data || null;
                showViewModal.value = true;
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed_to_load'));
            } finally {
                saving.value = false;
            }
        };

        const closeView = () => {
            showViewModal.value = false;
            viewItem.value = null;
        };

        const closeForm = () => {
            showFormModal.value = false;
        };

        const save = async () => {
            saving.value = true;
            try {
                const payload = {
                    member_type: form.value.member_type,
                    membership_category: form.value.membership_category,
                    organization_name: form.value.member_type === 'Corporate' ? (form.value.organization_name || null) : null,
                    customer_name: form.value.customer_name,
                    mobile_no: form.value.mobile_no,
                    email: form.value.email,
                    district_id: parseInt(form.value.district_id, 10),
                    is_city: !!form.value.is_city,
                    is_foreigner: !!form.value.is_foreigner,
                    country_name: form.value.is_foreigner ? (form.value.country_name || null) : null,
                    status: !!form.value.status,
                    lang: langValue(),
                };
                if (formMode.value === 'create') {
                    payload.password = form.value.password;
                    const res = await window.axios.post('/admin/api/customers', payload);
                    addToast('success', res.data?.message || t('success'));
                } else {
                    if (form.value.password) payload.password = form.value.password;
                    const res = await window.axios.put(`/admin/api/customers/${editingId.value}`, payload);
                    addToast('success', res.data?.message || t('success'));
                }
                showFormModal.value = false;
                await load();
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                saving.value = false;
            }
        };

        const toggleStatus = async (row) => {
            const id = row.CustomerID;
            busyIds.value = { ...busyIds.value, [id]: true };
            try {
                const res = await window.axios.put(`/admin/api/customers/${id}/status`, { status: !normalizeBool(row.Status), lang: langValue() });
                addToast('success', res.data?.message || t('success'));
                await load();
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                busyIds.value = { ...busyIds.value, [id]: false };
            }
        };

        const remove = async (id) => {
            if (!window.confirm(t('confirm_delete'))) return;
            busyIds.value = { ...busyIds.value, [id]: true };
            try {
                const res = await window.axios.delete(`/admin/api/customers/${id}`, { data: { lang: langValue() } });
                addToast('success', res.data?.message || t('success'));
                await load();
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                busyIds.value = { ...busyIds.value, [id]: false };
            }
        };

        onMounted(async () => {
            await loadDistricts();
            await load();
        });

        return {
            t,
            items,
            districts,
            pagination,
            page,
            perPage,
            statusFilter,
            searchQuery,
            loading,
            saving,
            busyIds,
            showFilters,
            toggleFilters,
            showColumns,
            toggleColumns,
            columns,
            visibleColumns,
            visibleColumnCount,
            colFilters,
            hasAnyFilter,
            clearFilters,
            showViewModal,
            viewItem,
            showFormModal,
            formMode,
            form,
            load,
            filteredItems,
            totalPages,
            pageButtons,
            showingText,
            goPage,
            nextPage,
            prevPage,
            goFirst,
            goLast,
            debouncedSearch,
            districtOptionLabel,
            districtLabel,
            applyConditional,
            openCreate,
            openEdit,
            openView,
            closeView,
            closeForm,
            save,
            toggleStatus,
            remove,
            toasts,
            removeToast,
        };
    },
};
</script>

<style scoped>
.page {
    display: flex;
    flex-direction: column;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.page-title {
    font-size: 28px;
    font-weight: 900;
    color: #0f172a;
    margin: 0;
}

.page-subtitle {
    margin: 6px 0 0 0;
    color: #64748b;
    font-size: 14px;
}

.header-actions {
    display: flex;
    gap: 10px;
    align-items: center;
}

.card {
    margin-top: 18px;
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.datatable-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 12px;
}

.toolbar-left {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
}

.length-select,
.status-select {
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #ffffff;
    padding: 0 10px;
    font-weight: 800;
    font-size: 12px;
}

.tool-btn {
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    padding: 0 12px;
    font-weight: 900;
    font-size: 12px;
    cursor: pointer;
}

.tool-btn-icon {
    width: 36px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.tool-btn-light {
    background: #f1f5f9;
    color: #0f172a;
}

.tool-btn-primary {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
}

.tool-btn-success {
    background: #16a34a;
    color: #ffffff;
    border-color: #16a34a;
}

.tool-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.columns-wrap {
    position: relative;
}

.columns-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    min-width: 200px;
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.12);
    border-radius: 12px;
    padding: 10px 10px;
    box-shadow: 0 18px 34px rgba(15, 23, 42, 0.12);
    z-index: 40;
}

.columns-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 8px;
    border-radius: 10px;
    cursor: pointer;
    font-weight: 800;
    color: #0f172a;
    font-size: 12px;
}

.columns-item:hover {
    background: rgba(241, 245, 249, 0.8);
}

.search-box {
    position: relative;
    max-width: 520px;
    width: 100%;
}

.search-box svg {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
}

.search-input {
    width: 100%;
    height: 36px;
    border-radius: 8px;
    border: 2px solid #2563eb;
    padding: 0 12px 0 42px;
    font-size: 13px;
    outline: none;
}

.search-input:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.table-container {
    overflow-x: auto;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
    table-layout: fixed;
}

.data-table thead {
    background: #0b2c3d;
}

.data-table th {
    padding: 10px 8px;
    text-align: center;
    font-size: 12px;
    font-weight: 900;
    color: #ffffff;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    vertical-align: top;
    white-space: nowrap;
}

.th-input,
.th-select {
    width: 100%;
    height: 30px;
    border-radius: 6px;
    border: 1px solid rgba(148, 163, 184, 0.9);
    background: #ffffff;
    padding: 0 8px;
    font-weight: 700;
    font-size: 12px;
    outline: none;
}

.data-table td {
    padding: 9px 8px;
    border-top: 1px solid #e2e8f0;
    vertical-align: top;
    color: #0f172a;
    font-size: 13px;
    background: #ffffff;
}

.data-table tbody tr:nth-child(even) td {
    background: rgba(241, 245, 249, 0.55);
}

.data-table tbody tr:hover td {
    background: rgba(226, 232, 240, 0.55);
}

.primary-title {
    font-weight: 700;
    color: #1a202c;
}

.primary-sub {
    color: #64748b;
    font-size: 12px;
    margin-top: 2px;
}

.tag {
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    display: inline-block;
}

.tag-success {
    background: #c6f6d5;
    color: #276749;
}

.tag-gray {
    background: #edf2f7;
    color: #4a5568;
}

.action-buttons {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
}

.btn {
    padding: 12px 18px;
    border: none;
    border-radius: 12px;
    font-weight: 700;
    cursor: pointer;
    font-size: 13px;
}

.btn-sm {
    padding: 8px 10px;
    border-radius: 10px;
    font-size: 12px;
}

.btn-primary {
    background: linear-gradient(135deg, #4A90E2 0%, #357ABD 100%);
    color: #ffffff;
}

.btn-secondary {
    background: #edf2f7;
    color: #1a202c;
}

.btn-danger {
    background: #fed7d7;
    color: #c53030;
}

.btn-warning {
    background: #feebc8;
    color: #7b341e;
}

.btn-success {
    background: #c6f6d5;
    color: #276749;
}

.form-input {
    width: 100%;
    padding: 12px 14px;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    font-size: 14px;
}

.empty {
    text-align: center;
    padding: 24px;
    color: #64748b;
}

.pager {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 2px 10px;
    flex-wrap: wrap;
}

.pager-left {
    color: #64748b;
    font-weight: 800;
    font-size: 12px;
}

.pager-right {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.pager-btn {
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #f1f5f9;
    color: #0f172a;
    padding: 7px 10px;
    border-radius: 6px;
    font-weight: 800;
    cursor: pointer;
    min-width: 66px;
}

.pager-num {
    min-width: 44px;
    height: 32px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #f1f5f9;
    color: #0f172a;
    border-radius: 6px;
    font-weight: 900;
    cursor: pointer;
}

.pager-num.active {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
}

.pager-btn:disabled,
.pager-num:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.skeleton-wrap {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.skeleton-row {
    height: 12px;
    border-radius: 999px;
    background: linear-gradient(90deg, rgba(226, 232, 240, 0.7), rgba(226, 232, 240, 0.25), rgba(226, 232, 240, 0.7));
    background-size: 200% 100%;
    animation: shimmer 1.2s infinite;
}

@keyframes shimmer {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}

.modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 18px;
    z-index: 9999;
}

.modal-card {
    width: 100%;
    max-width: 860px;
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 24px 70px rgba(0, 0, 0, 0.35);
    overflow: hidden;
}

.modal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 18px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.modal-title {
    font-size: 16px;
    font-weight: 800;
    color: #0f172a;
}

.modal-close {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    background: rgba(0, 0, 0, 0.02);
    cursor: pointer;
    font-size: 22px;
    line-height: 1;
    color: rgba(0, 0, 0, 0.75);
}

.modal-body {
    padding: 18px;
}

.modal-actions {
    padding: 16px 18px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.form-label {
    font-size: 13px;
    font-weight: 700;
    color: #334155;
}

.hint {
    margin-left: 8px;
    font-weight: 700;
    color: #64748b;
    font-size: 12px;
}

.details-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
}

.detail-label {
    font-size: 12px;
    color: #64748b;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.detail-value {
    margin-top: 6px;
    font-size: 14px;
    color: #0f172a;
    font-weight: 700;
}

.toast-container {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 10000;
}

.toast {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: white;
    border-radius: 12px;
    padding: 14px 16px;
    margin-bottom: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
    min-width: 320px;
}

.toast-success {
    border-left: 4px solid #48bb78;
}

.toast-error {
    border-left: 4px solid #f56565;
}

.toast-message {
    margin: 0;
    font-size: 13px;
    color: #0f172a;
    font-weight: 700;
}

.toast-close {
    background: none;
    border: none;
    cursor: pointer;
    color: #a0aec0;
    padding: 4px;
}

.toast-enter-active,
.toast-leave-active {
    transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
    opacity: 0;
    transform: translateX(20px);
}

@media (max-width: 900px) {
    .form-grid,
    .details-grid {
        grid-template-columns: 1fr;
    }
}
</style>
